import { useMutation, useQuery } from "@tanstack/react-query"
import { Bot, Check, ChevronLeft, Send } from "lucide-react"
import { useEffect, useState } from "react"
import { useLocation } from "react-router"

import { ErrorApi, pedir } from "@/api/cliente"
import {
  anterior,
  type Borrador,
  borradorVacio,
  PASOS,
  type Paso,
  PREGUNTA,
  puedeAvanzar,
  siguiente,
} from "@/componentes/soporte/guion"
import { PasoEvidencia, PasoRevision } from "@/componentes/soporte/PasosDeEvidencia"
import { limpiar, migas, senales } from "@/lib/bitacora"
import { imagenDelPortapapeles } from "@/lib/portapapeles"

/**
 * El asistente que levanta un reporte de falla.
 *
 * ⚠ **Es un guion, no un modelo.** Hace las mismas cuatro preguntas a todo el mundo
 * y clasifica el servidor leyendo lo que de verdad pasó — el código del sobre, el
 * estado HTTP, la excepción. No adivina, y por eso puede explicar siempre por qué
 * dijo lo que dijo. Ver `services/veredicto.py`.
 *
 * ⚠ **No modifica nada del sistema.** Escribe un reporte y abre una tarea de
 * desarrollo. No avanza expedientes, no toca dinero, no cambia estados.
 *
 * ⚠ **El último paso enseña TODO lo que se va a enviar.** La bitácora recoge rutas,
 * códigos y a veces valores que se teclearon en un formulario; mandarlos sin que
 * quien reporta los vea sería recogerlos a su espalda. Que pueda mirarlo es lo que
 * hace legítimo recogerlo.
 *
 * ⚠ **La bitácora se limpia al enviar.** Si no, el reporte siguiente arrastraría las
 * señales de éste y dev vería dos veces el mismo error atribuido a dos fallas.
 */

type Props = { onCerrar: () => void }

type Modulo = { clave: string; etiqueta: string; ruta: string }

export function AsistenteReporte({ onCerrar }: Props) {
  const ubicacion = useLocation()
  const vistaActual = ubicacion.pathname
  const [paso, setPaso] = useState<Paso>("VISTA")
  const [b, setB] = useState<Borrador>(() => borradorVacio(vistaActual))
  const [enviado, setEnviado] = useState<number | null>(null)

  // Las capturadas al abrir: congeladas, para que no cambien bajo los pies de quien
  // está marcando cuáles son suyas.
  const [capturadas] = useState(() => senales())
  const [recorrido] = useState(() => migas())

  /**
   * Ctrl+V pega la captura, desde CUALQUIER paso.
   *
   * ⚠ **En `document` y no en el paso de la evidencia.** Quien acaba de recortar la
   * pantalla con Win+Shift+S lo pega en cuanto abre esto, no cuatro preguntas
   * después; obligar a llegar al paso correcto convierte el atajo en un obstáculo.
   *
   * ⚠ **Pegar texto no toca el adjunto.** `imagenDelPortapapeles` devuelve `null`
   * salvo que el portapapeles traiga un archivo de imagen, así que escribir el
   * relato pegando de otro sitio sigue funcionando igual.
   */
  useEffect(() => {
    const alPegar = (e: ClipboardEvent) => {
      const imagen = imagenDelPortapapeles(e.clipboardData)
      if (imagen === null) return
      e.preventDefault()
      setB((previo) => ({ ...previo, captura: imagen }))
    }
    document.addEventListener("paste", alPegar)
    return () => document.removeEventListener("paste", alPegar)
  }, [])

  const menu = useQuery({
    queryKey: ["menu"],
    queryFn: () => pedir<Modulo[]>("/api/v1/menu"),
  })

  const enviar = useMutation({
    mutationFn: async () => {
      const cuerpo = new FormData()
      cuerpo.append(
        "datos",
        JSON.stringify({
          vista: b.vista,
          vista_al_reportar: vistaActual,
          titulo: b.titulo.trim(),
          que_intentaba: b.queIntentaba.trim(),
          que_paso: b.quePaso.trim(),
          senales: b.marcadas.map((i) => capturadas[i]).filter(Boolean),
          migas: recorrido,
          navegador: navigator.userAgent,
        }),
      )
      if (b.captura !== null) cuerpo.append("captura", b.captura)
      return pedir<{ id: number; tarea_id: number | null }>("/api/v1/reportes", {
        method: "POST",
        body: cuerpo,
      })
    },
    onSuccess: (r) => {
      setEnviado(r.id)
      limpiar()
    },
  })

  if (enviado !== null) {
    return (
      <div className="py-8 text-center">
        <Check
          size={28}
          aria-hidden={true}
          className="mx-auto mb-3"
          style={{ color: "var(--success)" }}
        />
        <h3 className="text-base font-semibold">Reporte #{enviado} enviado</h3>
        <p className="mx-auto mt-2 max-w-sm text-sm" style={{ color: "var(--fg-muted)" }}>
          Ya está en la lista de desarrollo con todo lo que hacía falta para reproducirlo. Alguien
          lo va a revisar y decidir qué se hace; no hace falta que lo escribas por otro lado.
        </p>
        <button type="button" onClick={onCerrar} className="boton-primario mt-5 h-9 px-3 text-sm">
          Listo
        </button>
      </div>
    )
  }

  const indice = PASOS.indexOf(paso)

  return (
    <div>
      {/* La conversación: lo que el asistente pregunta ahora. */}
      <div className="mb-4 flex items-start gap-3">
        <span
          aria-hidden={true}
          className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: "var(--primary-suave)", color: "var(--primary-texto)" }}
        >
          <Bot size={16} />
        </span>
        {/* ⚠ **Se anuncia, no se roba el foco.** En un asistente por pasos hay que
            enterarse de que la pregunta cambió; llevar el foco al campo lo consigue
            para quien ve la pantalla y desorienta a quien usa lector, que se pierde
            el enunciado. `aria-live` lo lee sin mover a nadie de sitio. */}
        <p className="flex-1 text-sm" aria-live="polite">
          {PREGUNTA[paso]}
        </p>
      </div>

      <div className="mb-5">
        {paso === "VISTA" && (
          <div>
            <button
              type="button"
              onClick={() => {
                setB({ ...b, vista: vistaActual })
                setPaso(siguiente(paso))
              }}
              data-testid="fue-aqui"
              className="boton-primario mb-3 h-9 w-full justify-start px-3 text-sm"
            >
              Sí, fue en esta pantalla ({vistaActual})
            </button>
            <label htmlFor="otra-vista" className="mb-1 block text-xs font-medium uppercase">
              O elige en cuál ocurrió
            </label>
            <select
              id="otra-vista"
              className="campo"
              value={b.vista === vistaActual ? "" : b.vista}
              onChange={(e) => setB({ ...b, vista: e.target.value })}
            >
              <option value="">Elige una…</option>
              {(menu.data ?? []).map((m) => (
                <option key={m.clave} value={m.ruta}>
                  {m.etiqueta} ({m.ruta})
                </option>
              ))}
            </select>
          </div>
        )}

        {paso === "INTENTO" && (
          <textarea
            aria-label="Qué estabas intentando hacer"
            rows={3}
            className="campo"
            value={b.queIntentaba}
            onChange={(e) => setB({ ...b, queIntentaba: e.target.value })}
            placeholder="Marcar la tarea TAR-2026-9 como hecha"
          />
        )}

        {paso === "RESULTADO" && (
          <textarea
            aria-label="Qué pasó"
            rows={3}
            className="campo"
            value={b.quePaso}
            onChange={(e) => setB({ ...b, quePaso: e.target.value })}
            placeholder="El botón se queda girando y la tarea sigue igual"
          />
        )}

        {paso === "TITULO" && (
          <input
            aria-label="Título del reporte"
            maxLength={160}
            className="campo"
            value={b.titulo}
            onChange={(e) => setB({ ...b, titulo: e.target.value })}
            placeholder="No puedo cerrar una tarea"
          />
        )}

        {paso === "EVIDENCIA" && (
          <PasoEvidencia capturadas={capturadas} borrador={b} onCambiar={setB} />
        )}

        {paso === "REVISION" && (
          <PasoRevision
            capturadas={capturadas}
            recorrido={recorrido}
            borrador={b}
            vistaAlReportar={vistaActual}
          />
        )}
      </div>

      {enviar.error && (
        <p role="alert" className="mb-3 text-sm" style={{ color: "var(--destructive)" }}>
          {enviar.error instanceof ErrorApi ? enviar.error.message : "No se pudo enviar"}
        </p>
      )}

      <div className="flex items-center gap-2">
        {indice > 0 && (
          <button
            type="button"
            onClick={() => setPaso(anterior(paso))}
            className="boton-secundario h-9 px-3 text-sm"
          >
            <ChevronLeft size={14} aria-hidden={true} />
            Atrás
          </button>
        )}
        <span className="flex-1 text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
          Paso {indice + 1} de {PASOS.length}
          {/* Se pega desde cualquier paso, así que hay que poder verlo desde
              cualquier paso: si no, pegar en el primero no da ninguna señal. */}
          {b.captura !== null && <span data-testid="hay-captura"> · captura adjunta</span>}
        </span>
        {paso === "REVISION" ? (
          <button
            type="button"
            disabled={enviar.isPending}
            onClick={() => enviar.mutate()}
            data-testid="enviar-reporte"
            className="boton-primario h-9 px-3 text-sm"
          >
            <Send size={14} aria-hidden={true} />
            {enviar.isPending ? "Enviando…" : "Enviar a desarrollo"}
          </button>
        ) : (
          <button
            type="button"
            disabled={!puedeAvanzar(paso, b)}
            onClick={() => setPaso(siguiente(paso))}
            data-testid="siguiente-paso"
            className="boton-primario h-9 px-3 text-sm"
          >
            Siguiente
          </button>
        )}
      </div>
    </div>
  )
}
